import React from "react";
import PropTypes from "prop-types";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const BurndownChart = ({ sprint, tasks, chartMode = "burndown" }) => {
  const getDatesInRange = (startDate, endDate) => {
    const dates = [];
    let currentDate = new Date(startDate);
    const lastDate = new Date(endDate);

    while (currentDate <= lastDate) {
      dates.push(new Date(currentDate).toLocaleDateString());
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
  };

  const calculateChartData = () => {
    const isTaskComplete = (task) => {
      const statusName =
        typeof task.status === "object" ? task.status?.name : task.status;

      return (
        statusName?.toLowerCase() === "closed" ||
        statusName?.toLowerCase() === "complete" ||
        statusName?.toLowerCase() === "done"
      );
    };

    const dates = getDatesInRange(sprint.start_date, sprint.end_date);
    const totalPoints = tasks.reduce(
      (sum, task) => sum + (task.points_estimate || 0),
      0
    );
    const pointsPerDay = totalPoints / (dates.length - 1);

    if (chartMode === "burndown") {
      const idealBurndown = dates.map((_, index) => {
        return Math.max(0, totalPoints - pointsPerDay * index);
      });

      const isTaskComplete = (task) =>
        typeof task.status === "object"
          ? task.status?.is_complete || task.status?.is_closed
          : task.status?.toLowerCase() === "complete";

      const pointsCompletedByDate = (date) => {
        const currentDate = new Date(date);
        return tasks
          .filter((task) => {
            if (!isTaskComplete(task)) return false;
            const completedAt = task.completed_at
              ? new Date(task.completed_at)
              : new Date(sprint.start_date);
            return completedAt <= currentDate;
          })
          .reduce((sum, task) => sum + (task.points_estimate || 0), 0);
      };

      const today = new Date();

      const actualBurndown = dates.map((date) => {
        const currentDate = new Date(date);
        if (currentDate < new Date(sprint.start_date)) return totalPoints;
        if (currentDate > today) return null;
        return Math.max(0, totalPoints - pointsCompletedByDate(date));
      });

      const actualBurnup = dates.map((date) => {
        const currentDate = new Date(date);
        if (currentDate < new Date(sprint.start_date)) return 0;
        if (currentDate > today) return null;
        return Math.min(totalPoints, pointsCompletedByDate(date));
      });

      return {
        labels: dates,
        datasets: [
          {
            label: "Ideal Burndown",
            data: idealBurndown,
            borderColor: "#6c757d",
            backgroundColor: "rgba(108, 117, 125, 0.1)",
            borderDash: [5, 5],
            fill: false,
          },
          {
            label: "Actual Burndown",
            data: actualBurndown,
            borderColor: "#007bff",
            backgroundColor: "rgba(0, 123, 255, 0.1)",
            fill: false,
          },
        ],
      };
    } else {
      const idealBurnup = dates.map((_, index) => {
        return Math.min(totalPoints, pointsPerDay * index);
      });

      const actualBurnup = dates.map((date) => {
        const currentDate = new Date(date);
        const sprintStartDate = new Date(sprint.start_date);

        if (currentDate < sprintStartDate) {
          return 0;
        }

        const completedTasks = tasks.filter((task) => {
          const isComplete =
            typeof task.status === "object"
              ? task.status?.is_complete || task.status?.is_closed
              : task.status?.name?.toLowerCase() === "complete" ||
                task.status?.toLowerCase() === "complete";
          return isComplete;
        });

        const completedPoints = completedTasks.reduce(
          (sum, task) => sum + (task.points_estimate || 0),
          0
        );

        return Math.min(totalPoints, completedPoints);
      });

      return {
        labels: dates,
        datasets: [
          {
            label: "Ideal Burnup",
            data: idealBurnup,
            borderColor: "#6c757d",
            backgroundColor: "rgba(108, 117, 125, 0.1)",
            borderDash: [5, 5],
            fill: false,
          },
          {
            label: "Actual Burnup",
            data: actualBurnup,
            borderColor: "#28a745",
            backgroundColor: "rgba(40, 167, 69, 0.1)",
            fill: false,
          },
        ],
      };
    }
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text:
            chartMode === "burndown" ? "Remaining Points" : "Completed Points",
        },
      },
      x: {
        title: {
          display: true,
          text: "Date",
        },
      },
    },
  };

  return (
    <div className="burndown-chart">
      <div className="chart-header">
        <h2>
          {chartMode === "burndown" ? "Sprint Burndown" : "Sprint Burnup"}
        </h2>
      </div>
      <div className="chart-container">
        <Line data={calculateChartData()} options={options} />
      </div>
    </div>
  );
};

BurndownChart.propTypes = {
  sprint: PropTypes.shape({
    start_date: PropTypes.string.isRequired,
    end_date: PropTypes.string.isRequired,
  }).isRequired,
  tasks: PropTypes.arrayOf(
    PropTypes.shape({
      points_estimate: PropTypes.number,
      status: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          is_complete: PropTypes.bool,
          name: PropTypes.string,
        }),
      ]),
    })
  ).isRequired,
  chartMode: PropTypes.oneOf(["burndown", "burnup"]),
};

export default BurndownChart;
